import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { http } from "../axios/axios";
import { GetImageUrl } from "../helpers/GetImageUrl";
import ProductCard from "../component/ProductCard";
import ProductListing from "./ProductListing";

const IpNetworkCatalogPage = () => {
  const { categorySlug, subCategorySlug } = useParams();
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selectedSubCategory, setSelectedSubCategory] = useState(null);
  const [loading, setLoading] = useState(true);
  const [subCategoryProducts, setSubCategoryProducts] = useState({});

  if (subCategorySlug) {
    return <ProductListing type="ip-network" />;
  }

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const res = await http.get("/navbar");
        const allCategories = res.data?.data?.ipNetworks || [];
        setCategories(allCategories);

        const category =
          allCategories.find((item) => item.slug === categorySlug) ||
          allCategories[0] ||
          null;
        setSelectedCategory(category);

        if (category) {
          const sub =
            category.subCategories?.find(
              (item) => item.slug === subCategorySlug,
            ) ||
            category.subCategories?.[0] ||
            null;
          setSelectedSubCategory(sub);
        } else {
          setSelectedSubCategory(null);
        }
      } catch (error) {
        console.error("IP NETWORKS MENU ERROR:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [categorySlug, subCategorySlug]);

  useEffect(() => {
    const subCategories = selectedCategory?.subCategories || [];

    if (!subCategories.length) {
      setSubCategoryProducts({});
      return;
    }

    let cancelled = false;

    const fetchSubCategoryProducts = async () => {
      try {
        const results = await Promise.all(
          subCategories.map((sub) =>
            http
              .get(`/products/ip-network/sub-category/${sub.slug}`, {
                params: { limit: 4 },
              })
              .then((res) => [sub.slug, res.data?.products || []])
              .catch(() => [sub.slug, []]),
          ),
        );

        if (cancelled) return;

        setSubCategoryProducts(Object.fromEntries(results));
      } catch (error) {
        console.error("IP NETWORK SUBCATEGORY PRODUCTS ERROR:", error);
      }
    };

    fetchSubCategoryProducts();

    return () => {
      cancelled = true;
    };
  }, [selectedCategory]);

  const pageTitle =
    selectedSubCategory?.name || selectedCategory?.name || "IP Networks";
  const pageDescription =
    selectedSubCategory?.description ||
    selectedCategory?.description ||
    "Explore IP network solutions and infrastructure categories.";

  const categoryLink = useMemo(() => {
    if (!selectedCategory) return "/it-products";
    return `/it-products/category/${selectedCategory.slug}`;
  }, [selectedCategory]);

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 p-10 text-center text-slate-600">
        Loading IP Networks...
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 text-slate-800">
      <Helmet>
        <title>{pageTitle} | Suraksha IP Networks</title>
        <meta name="description" content={pageDescription} />
      </Helmet>

      <div className="max-w-7xl mx-auto px-4 py-10 sm:px-6 lg:px-8">
        {selectedCategory && (
          <section className="mb-10 overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
            <div className="grid md:grid-cols-2 gap-0">
              <div className="p-8 md:p-12">
                <span className="inline-flex items-center rounded-full bg-blue-50 px-3 py-1 text-xs font-semibold uppercase tracking-[0.2em] text-blue-700">
                  IP Networks
                </span>
                <h1 className="mt-5 text-3xl md:text-5xl font-black tracking-tight text-slate-900">
                  {selectedCategory.name}
                </h1>
                <p className="mt-5 text-base text-slate-600 leading-7">
                  {selectedCategory.description || pageDescription}
                </p>
              </div>
              {selectedCategory.image ? (
                <div className="h-72 md:h-full min-h-[280px] bg-slate-100">
                  <img
                    src={GetImageUrl(selectedCategory.image)}
                    alt={selectedCategory.name}
                    className="h-full w-full object-cover"
                  />
                </div>
              ) : null}
            </div>
          </section>
        )}

        <div className="grid lg:grid-cols-[260px_minmax(0,1fr)] gap-8">
          <aside className="lg:sticky lg:top-28 h-fit">
            <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
              <h2 className="text-lg font-bold uppercase tracking-wide text-slate-900">
                Categories
              </h2>
              <div className="mt-4 space-y-2">
                {categories.map((category) => (
                  <Link
                    key={category._id}
                    to={`/it-products/category/${category.slug}`}
                    className={`block rounded-xl px-4 py-3 text-sm font-semibold transition ${category.slug === selectedCategory?.slug ? "bg-blue-600 text-white" : "bg-slate-50 text-slate-700 hover:bg-slate-100"}`}
                  >
                    {category.name}
                  </Link>
                ))}
              </div>
            </div>
          </aside>

          <main>
            {selectedCategory?.subCategories?.length ? (
              <div className="space-y-6">
                <div className="flex items-center justify-between gap-3">
                  <h2 className="text-2xl font-black uppercase tracking-tight text-slate-900">
                    Available Subcategories
                  </h2>
                  <span className="rounded-full bg-slate-100 px-3 py-1 text-xs font-semibold uppercase text-slate-600">
                    {selectedCategory.subCategories.length} items
                  </span>
                </div>

                <div className="space-y-10">
                  {selectedCategory.subCategories.map((sub) => {
                    const products = subCategoryProducts[sub.slug] || [];

                    return (
                      <div
                        key={sub._id}
                        className="overflow-hidden rounded-2xl border border-slate-200 bg-white p-5 shadow-sm"
                      >
                        <div className="flex flex-col gap-4 sm:flex-row sm:items-center">
                          <div className="h-20 w-20 shrink-0 overflow-hidden rounded-xl bg-slate-100">
                            <img
                              src={GetImageUrl(
                                sub.image || selectedCategory.image,
                              )}
                              alt={sub.name}
                              className="h-full w-full object-cover"
                            />
                          </div>

                          <div className="flex-1">
                            <h3 className="text-base font-bold text-slate-900">
                              {sub.name}
                            </h3>
                            <p className="mt-1 text-sm text-slate-600 line-clamp-2">
                              {sub.description ||
                                "Explore IP network solutions in this category."}
                            </p>
                          </div>

                          <Link
                            to={`/it-products/sub-category/${sub.slug}`}
                            className="inline-flex items-center justify-center rounded-xl bg-blue-600 px-4 py-2 text-xs font-semibold uppercase tracking-wide text-white transition hover:bg-blue-700"
                          >
                            View All
                          </Link>
                        </div>

                        {products.length > 0 ? (
                          <div className="mt-5 grid grid-cols-2 gap-4 sm:grid-cols-3 xl:grid-cols-4">
                            {products.map((product) => (
                              <ProductCard key={product._id} item={product} />
                            ))}
                          </div>
                        ) : (
                          <div className="mt-5 rounded-xl border border-dashed border-slate-300 bg-slate-50 p-6 text-center text-sm text-slate-500">
                            No products found in this subcategory yet.
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            ) : (
              <div className="rounded-2xl border border-dashed border-slate-300 bg-white p-10 text-center text-slate-600">
                No subcategories available for this section yet.
              </div>
            )}
          </main>
        </div>
      </div>
    </div>
  );
};

export default IpNetworkCatalogPage;
